import React from 'react';

import { SimpleGrid, VStack, Text, Box } from '@chakra-ui/react';
import { formatDate, formatDateTime } from 'utils/date.util';

import { DetailField, DetailSection } from './DetailField';
import { useColors } from './colors';

const formatAddress = (addr) => {
  if (!addr) return '—';
  const lines = [
    addr.addressLine1,
    addr.addressLine2,
    [addr.city, addr.stateOrProvince || addr.stateOrRegion, addr.postalCode]
      .filter(Boolean)
      .join(', '),
    addr.countryCode,
  ].filter(Boolean);
  return lines.join('\n');
};

const BuyerShippingTab = ({ order }) => {
  const { subTextColor, cardBg, borderColor, textColor } = useColors();
  const isEbay = order.marketplace === 'ebay';

  const shipTo = isEbay
    ? order.fulfillmentStartInstructions?.shippingStep?.shipTo
    : order.shipTo;

  const shipToName = isEbay ? shipTo?.fullName : order.buyer?.buyerName;
  const shipToAddress = isEbay ? shipTo?.contactAddress : shipTo;
  const phone = isEbay ? shipTo?.primaryPhone?.phoneNumber : undefined;

  return (
    <VStack align="stretch" spacing={5}>
      <DetailSection title="Buyer">
        {isEbay ? (
          <>
            <DetailField label="eBay username" value={order.buyer?.username} mono />
            <DetailField
              label="Registration location"
              value={
                order.buyer?.buyerRegistrationAddress
                  ? `${order.buyer.buyerRegistrationAddress.city}, ${order.buyer.buyerRegistrationAddress.stateOrProvince}`
                  : '—'
              }
            />
          </>
        ) : (
          <>
            <DetailField label="Buyer name" value={order.buyer?.buyerName} />
            <DetailField label="Buyer email" value={order.buyer?.buyerEmail} />
            <DetailField
              label="Purchase order #"
              value={order.buyer?.buyerPurchaseOrderNumber}
              mono
            />
            <DetailField label="Company" value={order.buyer?.buyerCompanyName} />
          </>
        )}
      </DetailSection>

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={5}
      >
        <Text fontWeight="semibold" color={textColor} fontSize="md" mb={4}>
          Ship-to address
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <DetailField label="Recipient" value={shipToName} />
          <DetailField label="Phone" value={phone} mono />
          <Box gridColumn={{ md: 'span 2' }}>
            <Text fontSize="xs" color={subTextColor} mb={1}>
              Address
            </Text>
            <Text fontSize="sm" color={textColor} whiteSpace="pre-line" fontWeight="medium">
              {formatAddress(shipToAddress)}
            </Text>
          </Box>
        </SimpleGrid>
      </Box>

      {isEbay && order.fulfillmentStartInstructions?.shippingStep && (
        <DetailSection title="Shipping service (eBay)">
          <DetailField
            label="Carrier"
            value={order.fulfillmentStartInstructions.shippingStep.shippingCarrierCode}
          />
          <DetailField
            label="Service code"
            value={order.fulfillmentStartInstructions.shippingStep.shippingServiceCode}
          />
          <DetailField
            label="Est. delivery (min)"
            value={
              order.fulfillmentStartInstructions.shippingStep.minEstimatedDeliveryDate
                ? formatDate(
                    order.fulfillmentStartInstructions.shippingStep.minEstimatedDeliveryDate,
                  )
                : '—'
            }
          />
          <DetailField
            label="Est. delivery (max)"
            value={
              order.fulfillmentStartInstructions.shippingStep.maxEstimatedDeliveryDate
                ? formatDate(
                    order.fulfillmentStartInstructions.shippingStep.maxEstimatedDeliveryDate,
                  )
                : '—'
            }
          />
        </DetailSection>
      )}


      {!isEbay && order.fulfillment && (
        <DetailSection title="Fulfillment windows (Amazon)">
          <DetailField label="Ship service level" value={order.fulfillment.shipServiceLevel} />
          <DetailField
            label="Latest ship date"
            value={
              order.fulfillment.latestShipDate
                ? formatDateTime(order.fulfillment.latestShipDate)
                : '—'
            }
          />
          <DetailField
            label="Earliest delivery"
            value={
              order.fulfillment.earliestDeliveryDate
                ? formatDate(order.fulfillment.earliestDeliveryDate)
                : '—'
            }
          />
          <DetailField
            label="Latest delivery"
            value={
              order.fulfillment.latestDeliveryDate
                ? formatDate(order.fulfillment.latestDeliveryDate)
                : '—'
            }
          />
        </DetailSection>
      )}
    </VStack>
  );
};

export default BuyerShippingTab;
